import { Component, computed, effect, inject, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AnnouncementsService } from '../services/announcements.service';

@Component({
  selector: 'app-announcements-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './announcements-overlay.component.html',
  styleUrls: ['./announcements-overlay.component.scss']
})
export class AnnouncementsOverlayComponent implements AfterViewInit {
  @ViewChild('marqueeContent', { static: false }) marqueeContent!: ElementRef<HTMLElement>;
  @ViewChild('marqueeContainer', { static: false }) marqueeContainer!: ElementRef<HTMLElement>;
  
  private announcementsService = inject(AnnouncementsService);
  
  activeAnnouncements = this.announcementsService.getActiveAnnouncements();
  
  infiniteAnnouncements = computed(() => {
    const announcements = this.activeAnnouncements();
    if (announcements.length === 0) return [];
    
    return [...announcements, ...announcements, ...announcements, ...announcements, ...announcements];
  });

  private animationId: number | null = null;
  private startTime: number = 0;
  private isAnimating = false;

  constructor() {
    effect(() => {
      const announcements = this.activeAnnouncements();
      console.log('Active announcements updated:', announcements);
      if (announcements.length > 0 && !this.isAnimating) {
        this.startAnimation();
      }
    });
  }

  ngAfterViewInit() {
    if (this.activeAnnouncements().length > 0) {
      this.startAnimation();
    }
  }

  private startAnimation() {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    this.startTime = performance.now();
    
    const animate = (currentTime: number) => {
      if (!this.marqueeContent?.nativeElement) return;
      
      const elapsed = currentTime - this.startTime;
      const duration = this.getAnimationDuration() * 1000; 
      const startX = this.marqueeContainer?.nativeElement?.offsetWidth ?? window.innerWidth;
      const endX = -this.marqueeContent.nativeElement.scrollWidth;
      const currentX = startX - (elapsed / duration) * (startX - endX);
      
      this.marqueeContent.nativeElement.style.transform = `translateX(${currentX}px)`;
      
      if (elapsed < duration) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.startTime = performance.now();
        this.animationId = requestAnimationFrame(animate);
      }
    };
    
    this.animationId = requestAnimationFrame(animate);
  }

  private getAnimationDuration(): number {
    const announcements = this.activeAnnouncements();
    if (announcements.length === 0) return 1440; // 480 * 2 * 1.5
    
    const baseTime = 720; // 240 * 2 * 1.5
    const additionalTime = announcements.length * 120; // 40 * 2 * 1.5
    return baseTime + additionalTime;
  }

  ngOnDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
}
